import { Tooltip } from '@chakra-ui/react';
import { DateTime, DateTimeFormatOptions } from 'luxon';
import React, { useMemo } from 'react';

import { Util } from '@finley-technologies/finley-types';

import { Typography } from '~/packages/common-frontend/components/Typography';

// Constants
export const DEFAULT_SHORT_LOCALE_OPTIONS: DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
};
const DEFAULT_TOOLTIP_LOCALE_OPTIONS: DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
};

// Exports
type TimestampProps = {
  timestamp: string | Util.FullDateTimeStr | number | Date | DateTime;
  variant: 'datetime' | 'timeago';
  showDot?: boolean;
  /** Change date display format when variant="datetime". See Luxon `toLocaleString()` docs */
  shortLocaleFormat?: DateTimeFormatOptions;
  /** Change date display format in the tooltip. See Luxon `toLocaleString()` docs */
  tooltipLocaleFormat?: DateTimeFormatOptions;
  TooltipProps?: React.ComponentProps<typeof Tooltip>;
  TypographyProps?: React.ComponentProps<typeof Typography>;
};
/**
 * Variants:
 * - `datetime`: Display the timestamp as a formatted date-time string, e.g. "Feb 25, 2025 at 5:11 PM"
 * - `timeago`: Display the timestamp as a relative "time ago" string, e.g. "2 minutes ago" or "3 days ago"
 *
 * Pass `showDot=true` to show a `·` in front of the timestamp. Useful when you want to visually
 * separate a timestamp from other inline text content.
 *
 * Pass `shortLocaleFormat` and `tooltipLocaleFormat` props to change the format of the rendered
 * date (when `variant="datetime"`) and the tooltip date strings. See Luxon docs for `DateTimeFormatOptions`.
 */
export const Timestamp = ({
  timestamp,
  variant = 'datetime',
  showDot = false,
  shortLocaleFormat = DEFAULT_SHORT_LOCALE_OPTIONS,
  tooltipLocaleFormat = DEFAULT_TOOLTIP_LOCALE_OPTIONS,
  TooltipProps,
  TypographyProps,
}: TimestampProps) => {
  const dates = useMemo(() => {
    const dateTime = (() => {
      if (typeof timestamp === 'number') return DateTime.fromMillis(timestamp);
      if (typeof timestamp === 'string') return DateTime.fromISO(timestamp);
      if (timestamp instanceof Date) return DateTime.fromJSDate(timestamp);
      if (timestamp instanceof DateTime) return timestamp;
      throw Error(
        `Unexpected [timestamp=${timestamp}]. Expected ISO string, epoch milliseconds, JS or Luxon date`,
      );
    })();
    const tooltipTimestamp = dateTime.toLocaleString(tooltipLocaleFormat);
    const shortTimestamp =
      variant === 'timeago'
        ? dateTime.toRelative({ base: DateTime.now() })
        : dateTime.toLocaleString(shortLocaleFormat);
    return { shortTimestamp, tooltipTimestamp };
  }, [shortLocaleFormat, timestamp, tooltipLocaleFormat, variant]);

  return (
    <Tooltip openDelay={300} label={dates.tooltipTimestamp} {...TooltipProps}>
      <span>
        <Typography inline variant="detail-muted" {...TypographyProps}>
          {showDot && <span> · </span>}
          {dates.shortTimestamp}
        </Typography>
      </span>
    </Tooltip>
  );
};
